// AI-assisted OCR fallback for genuinely low-confidence results - see
// utils/ocr.ts, which calls this only when Cloud Vision's own result scored
// below the low-confidence threshold. A multimodal LLM reads text using
// visual/contextual understanding rather than pure character segmentation,
// which tends to do better on stylized or decorative fonts (e.g. menu
// titles) that trip up segmentation-based OCR engines.
//
// Requires a signed-in Cognito session (the backend verifies the ID token
// and enforces a small per-user daily limit, since each call has a real
// per-request cost) - unlike the primary Cloud Vision path, this one fails
// closed rather than being open to anonymous use.
import { resizeAndEncodeAsJpeg } from "./imageEncoding";

const PUBLIC_API_URL = "https://d29a52ery9.execute-api.us-east-1.amazonaws.com/prod";
const OCR_LLM_URL = `${PUBLIC_API_URL}/ocr/llm`;

export interface LlmOcrResult {
    text: string;
}

export async function recognizeJapaneseTextViaLlm(source: File | Blob, idToken: string): Promise<LlmOcrResult> {
    const imageBase64 = await resizeAndEncodeAsJpeg(source);

    const response = await fetch(OCR_LLM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ image: imageBase64 }),
    });
    if (!response.ok) throw new Error(`AI-assisted OCR request failed (${response.status})`);

    const data = await response.json();
    if (typeof data.text !== "string") throw new Error("Malformed AI-assisted OCR response");

    return { text: data.text };
}
